// @ts-nocheck
import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import { Clock } from '@phosphor-icons/react';
import { hasTripStarted, getTimeUntilTrip } from '../../helpers/dates';
import './TripCountdown.css';

const TripCountdown = ({ id, tripStartDate, ...rest }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [isVisible, setIsVisible] = useState(true);

  const updateCountdown = useCallback(() => {
    try {
      // Check if trip has started
      if (hasTripStarted(tripStartDate)) {
        setIsVisible(false);
        return;
      }

      // Get time remaining
      const timeRemaining = getTimeUntilTrip(tripStartDate);

      if (!timeRemaining) {
        setIsVisible(false);
        return;
      }

      // Round up hours if there are any minutes or seconds remaining
      const roundedHours =
        timeRemaining.minutes > 0 || timeRemaining.seconds > 0
          ? timeRemaining.hours + 1
          : timeRemaining.hours;

      // Handle hour overflow (if rounded hours >= 24, add to days and reset hours)
      let adjustedDays = timeRemaining.days;
      let adjustedHours = roundedHours;

      if (adjustedHours >= 24) {
        adjustedDays += Math.floor(adjustedHours / 24);
        adjustedHours = adjustedHours % 24;
      }

      setTimeLeft({
        ...timeRemaining,
        days: adjustedDays,
        hours: adjustedHours
      });
      setIsVisible(true);
    } catch (error) {
      console.error('Countdown update error:', error);
      setIsVisible(false);
    }
  }, [tripStartDate]);

  useEffect(() => {
    // Initial calculation
    updateCountdown();

    // Set up interval to update every second
    const timer = setInterval(updateCountdown, 1000);

    // Cleanup function to clear interval
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [updateCountdown]);

  // Handle visibility change to keep countdown accurate when tab becomes active
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        updateCountdown();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [updateCountdown]);

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  // Format numbers with leading zeros
  const formatNumber = (num) => num.toString().padStart(2, '0');

  return (
    <div className='trip-countdown' id={id} {...rest}>
      <div className='trip-countdown__content'>
        <p className='trip-countdown__subtitle'>
          Your magical journey begins in...
        </p>

        <div className='trip-countdown__timers'>
          <div className='trip-countdown__timer'>
            <div className='trip-countdown__number'>
              {formatNumber(timeLeft?.days)}
            </div>
            <div className='trip-countdown__label'>DAYS</div>
          </div>

          <div className='trip-countdown__timer'>
            <div className='trip-countdown__number'>
              {formatNumber(timeLeft?.hours)}
            </div>
            <div className='trip-countdown__label'>HOURS</div>
          </div>
        </div>

        <div className='trip-countdown__message'>
          <Clock size={16} weight='regular' />
          <span>Get ready for adventure!</span>
        </div>
      </div>
    </div>
  );
};

TripCountdown.propTypes = {
  id: PropTypes.string.isRequired,
  tripStartDate: PropTypes.string.isRequired
};

export default TripCountdown;
